import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, Calendar, X } from 'lucide-react';

const CalendarTimePicker = ({ controls = ['calendar', 'time'], onClose, onConfirm }) => {
  const today = new Date();
  const currentTime = today.toTimeString().split(" ")[0];
  const defaultDate = new Date();
  defaultDate.setDate(today.getDate() + 5);

  const [selectedDate, setSelectedDate] = useState(defaultDate);
  const [selectedTime, setSelectedTime] = useState(currentTime);
  const [currentMonth, setCurrentMonth] = useState(defaultDate);
  const showCalendar = controls.includes('calendar');
  const showTime = controls.includes('time');
  const [muhuratInfo, setMuhuratInfo] = useState('');

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };
  const handleDateClick = (date) => {
    if (!isPastDate(date)) {
      setSelectedDate(date);
    }
  };
  const handlePrevMonth = () => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
    // Only allow navigation if the previous month is not before the current month
    if (prevMonth.getMonth() >= today.getMonth() && prevMonth.getFullYear() >= today.getFullYear()) {
      setCurrentMonth(prevMonth);
    }
  };
  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };
  const canNavigatePrev = () => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
    return prevMonth.getMonth() >= today.getMonth() && prevMonth.getFullYear() >= today.getFullYear();
  };
  const handleTimeChange = (time) => {
    setSelectedTime(time);
    // setIsTimeDropdownOpen(false);
  };

  const handleConfirm = () => {
    const finalDate = selectedDate || defaultDate; // use selected date OR fallback
    onConfirm?.({ date: finalDate, time: selectedTime, muhuratInfo });
  };

  const isToday = (date) => {
    const today = new Date();
    return date &&
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date) => {
    return date &&
      selectedDate &&
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear();
  };
  const isPastDate = (date) => {
    if (!date) return false;
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0); // Reset to start of today
    tomorrow.setDate(tomorrow.getDate() + 5); // Move to tomorrow
    return date < tomorrow;
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto animate-fadeIn">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleUp {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }
        .animate-scaleUp {
          animation: scaleUp 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>

      {/* Modal Container */}
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl relative overflow-hidden border border-gray-100 flex flex-col my-8 animate-scaleUp">

        {/* Close Button */}
        <button
          type="button"
          className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-[#9F2225] hover:bg-[#851d20] text-white active:scale-90 transition-all duration-150 shadow-md shadow-red-900/10"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        {/* Modal Header */}
        <div className="bg-gradient-to-r from-[#9F2225]/5 to-[#723F25]/5 px-6 py-5 border-b border-gray-100 flex items-center justify-center gap-2.5 w-full text-center">
          <Calendar className="w-5 h-5 text-[#9F2225]" />
          <h2 className="text-lg font-bold text-gray-900 leading-tight">Select Date & Time</h2>
        </div>

        {/* Modal Body - Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">

          {/* Left Column: Calendar */}
          <div className="p-5 sm:p-6">
            {showCalendar && (
              <div className="space-y-4">
                {/* Calendar Header / Month Toggle */}
                <div className="flex items-center justify-between">
                  {canNavigatePrev() ? (
                    <button
                      type="button"
                      onClick={handlePrevMonth}
                      className="p-2 hover:bg-gray-100 active:scale-90 rounded-xl transition-all duration-150 border border-gray-100"
                    >
                      <ChevronLeft className="w-4 h-4 text-gray-600" />
                    </button>
                  ) : (
                    <div className="w-8 h-8"></div>
                  )}
                  <h3 className="text-sm font-semibold text-gray-800">
                    {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </h3>
                  <button
                    type="button"
                    onClick={handleNextMonth}
                    className="p-2 hover:bg-gray-100 active:scale-90 rounded-xl transition-all duration-150 border border-gray-100"
                  >
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </button>
                </div>

                {/* Days of Week */}
                <div className="grid grid-cols-7 gap-1">
                  {daysOfWeek.map((day) => (
                    <div key={day} className="text-center text-xs font-semibold text-gray-500 py-1">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Days Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {days.map((date, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => date && handleDateClick(date)}
                      disabled={!date || isPastDate(date)}
                      className={`
                        w-full aspect-square flex items-center justify-center rounded-xl text-xs font-semibold transition-all duration-150
                        ${!date ? 'invisible' : ''}
                        ${isPastDate(date)
                          ? 'text-gray-300 cursor-not-allowed bg-gray-50/50'
                          : isSelected(date)
                            ? 'bg-[#9F2225] text-white shadow-md shadow-red-900/10'
                            : isToday(date)
                              ? 'bg-red-50 text-[#9F2225] hover:bg-red-100 border border-red-200/50'
                              : 'text-gray-700 hover:bg-gray-50'
                        }
                        ${date && !isSelected(date) && !isPastDate(date) ? 'hover:scale-105 active:scale-95' : ''}
                      `}
                    >
                      {date ? date.getDate() : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Time */}
          <div className="bg-gray-50/50 p-5 sm:p-6 space-y-4">
            {showTime && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Select Time</h4>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Time</label>
                  <input
                    type="time"
                    step="1"
                    value={selectedTime}
                    onChange={(e) => handleTimeChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm text-gray-700 bg-white focus:border-[#9F2225] focus:ring-2 focus:ring-[#9F2225]/15 transition-all duration-200 outline-none h-11"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Additional info on muhurat if any</label>
                  <input
                    type="text"
                    value={muhuratInfo}
                    onChange={(e) => setMuhuratInfo(e.target.value)}
                    placeholder="Example: 8 - 10 AM"
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm text-gray-700 bg-white focus:border-[#9F2225] focus:ring-2 focus:ring-[#9F2225]/15 transition-all duration-200 outline-none h-11"
                  />
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-gray-50/70 border-t border-gray-100">
          <button
            type="button"
            onClick={handleConfirm}
            className="w-full bg-[#9F2225] hover:bg-[#851d20] text-sm text-white py-3 px-4 rounded-xl active:scale-98 transition-all duration-200 font-semibold shadow-md shadow-red-900/10 uppercase tracking-wider"
          >
            Confirm Selection
          </button>
        </div>

      </div>
    </div>
  );
};
export default CalendarTimePicker;






